import { utils as utilsCore } from '@industriousapps/excelkits-core';
import pipe from '@/utils/pipe';
import parseSelectOptions, { encodeSelectOptions, GENERIC_BOOLEAN_OPTIONS } from '@/utils/reporting/parseSelectOptions';
import { isValidBoolean } from '@/utils/boolean';
import getNormalizedValueType from '@/utils/reporting/getNormalizedValueType';
const {
  isValidNumber
} = utilsCore; // Ensure that all question layouts
// have the required attributes to render

export default function updateQuestionLayout(question) {
  const {
    layout
  } = question;
  const valueType = getNormalizedValueType(question.valueType);
  const result = {};
  return pipe(updateCheckbox, updateSelectOrRadio, updateHidden, updateNumberOrMoney, updateSlider, removeInvalidDefaultValue)(result, {
    layout,
    valueType,
    question
  });
  return result;
} // Set required attributes
// on checkbox questions

function updateCheckbox(result, _ref) {
  let {
    layout,
    valueType,
    question
  } = _ref;
  if (layout !== 'checkbox') return result; // Strings

  if (valueType === 'string' && !isString(result.checkedValue)) {
    result.checkedValue = `${typeof question.defaultValue === 'string' ? question.defaultValue : ''}`;
  }

  if (valueType === 'string' && !isString(result.uncheckedValue)) {
    result.uncheckedValue = '';
  } // Numbers


  if (valueType === 'integer' && !isNum(result.checkedValue)) {
    result.checkedValue = typeof question.defaultValue === 'number' ? question.defaultValue : 1;
  }

  if (valueType === 'integer' && !isNum(result.uncheckedValue)) {
    result.uncheckedValue = 0;
  } // Booleans


  if (valueType === 'boolean' && !isBool(result.checkedValue)) {
    result.checkedValue = true;
  }

  if (valueType === 'boolean' && !isBool(result.uncheckedValue)) {
    result.uncheckedValue = false;
  }

  return result;
} // Set required options on Select
// questions when it becomes layout target


function updateSelectOrRadio(result, _ref2) {
  let {
    layout,
    valueType,
    question
  } = _ref2;

  if (['select', 'radio', 'radioButtons'].includes(layout) === false) {
    return result;
  }

  const {
    defaultValue
  } = question;
  const currentOptions = parseSelectOptions(question.options || '');
  const hasNoOptions = currentOptions.length === 0;
  const hasBooleanOptions = currentOptions.map(_ref3 => {
    let {
      value
    } = _ref3;
    return value;
  }).every(value => isValidBoolean(value));
  const hasNumericOptions = currentOptions.map(_ref4 => {
    let {
      value
    } = _ref4;
    return value;
  }).every(value => isValidNumber(value)); // String

  if (valueType === 'string' && (hasNoOptions || hasBooleanOptions || hasNumericOptions)) {
    result.options = defaultValue ? encodeSelectOptions([{
      value: `${defaultValue}`,
      content: 'Default'
    }]) : encodeSelectOptions([{
      value: 'option',
      content: 'Option'
    }]);
  } // Number


  if (valueType === 'integer' && (hasNoOptions || !hasNumericOptions)) {
    result.options = getSelectNumOptions(question);
  } // Boolean


  if (valueType === 'boolean' && (hasNoOptions || !hasBooleanOptions)) {
    result.options = GENERIC_BOOLEAN_OPTIONS;
  }

  return result;
} // Set required default value on Hidden
// questions when it becomes layout target


function updateHidden(result, _ref5) {
  let {
    layout,
    valueType,
    question
  } = _ref5;
  if (layout !== 'hidden') return result;
  const {
    defaultValue
  } = question;
  const hasNoDefaultValue = defaultValue === undefined || defaultValue === null || defaultValue !== defaultValue; // NaN check
  // String

  if (valueType === 'string' && hasNoDefaultValue) {
    result.defaultValue = '';
  } // Number


  if (valueType === 'integer' && (hasNoDefaultValue || !isValidNumber(defaultValue))) {
    result.defaultValue = 0;
  } // Boolean


  if (valueType === 'boolean' && (hasNoDefaultValue || !isValidBoolean(`${defaultValue}`))) {
    result.defaultValue = false;
  }

  return result;
} // Set required attributes on Number/Money
// questions when it becomes layout target


function updateNumberOrMoney(result, _ref6) {
  let {
    layout,
    question
  } = _ref6;
  if (['number', 'money'].includes(layout) === false) return result;
  const {
    step
  } = question; // Require step

  if (!isNum(step)) {
    result.step = 1;
  }

  return result;
} // Set required attributes on Slider
// questions when it becomes layout target


function updateSlider(result, _ref7) {
  let {
    layout,
    question
  } = _ref7;
  if (layout !== 'slider') return result;
  const {
    step,
    defaultValue,
    minimumValue,
    maximumValue
  } = question; // Require step

  if (!isNum(step)) {
    result.step = 1;
  } // Require minimum


  if (!isNum(minimumValue)) {
    result.minimumValue = 0;
  } // Require maximum


  if (!isNum(maximumValue)) {
    result.maximumValue = (isNum(defaultValue) ? defaultValue : 0) + (isNum(result.step) ? result.step : step || 1);
  }

  return result;
} // Remove invalid default values
// when not required to render question


function removeInvalidDefaultValue(result, _ref8) {
  let {
    layout,
    valueType,
    question
  } = _ref8;
  const {
    defaultValue
  } = question;
  const hasReqDefaultValue = ['hidden'].includes(layout);
  const ignoresDefaultValue = ['checkbox'].includes(layout);

  if (hasReqDefaultValue || ignoresDefaultValue || isUndefined(defaultValue)) {
    return result;
  } // Remove invalid numeric default value


  if (valueType === 'integer' && !isValidNumber(defaultValue, false)) {
    result.defaultValue = null;
  } // Remove invalid boolean default value


  if (valueType === 'boolean' && !isValidBoolean(`${defaultValue}`)) {
    result.defaultValue = null;
  }

  return result;
}

function isString(val) {
  return typeof val === 'string';
}

function isNum(val) {
  return typeof val === 'number' && !isNaN(val);
}

function isBool(val) {
  return typeof val === 'boolean';
}

function isUndefined(val) {
  return typeof val === 'undefined';
} // Recover any usable value from question
// prefering: 1st default value, 2nd min/max
// and falling back to the number 1


function getSelectNumOptions(question) {
  const {
    defaultValue
  } = question;
  const hasDefault = typeof defaultValue === 'number';
  const min = question.minimumValue;
  const max = question.maximumValue;
  const hasMin = typeof min === 'number' && !isNaN(min);
  const hasMax = typeof max === 'number' && !isNaN(max);

  if (hasDefault) {
    return encodeSelectOptions([{
      value: `${defaultValue}`,
      content: 'Default'
    }]);
  } else if (hasMin || hasMax) {
    const options = [hasMin ? {
      value: `${min}`,
      content: 'Minimum'
    } : null, hasMax ? {
      value: `${max}`,
      content: 'Maximum'
    } : null].filter(Boolean);
    return encodeSelectOptions(options);
  } // Fallback for no default, min, or max


  return encodeSelectOptions([{
    value: '1',
    content: 'One'
  }]);
}